import type { User } from '@supabase/supabase-js'
import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { supabase } from '@/integrations/supabase/client'

type AuthContextType = {
  user: User | null
  isLoading: boolean
  hasOnboarded: boolean | null
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  hasOnboarded: null,
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasOnboarded, setHasOnboarded] = useState<boolean | null>(null)

  const checkOnboarding = useCallback(async (_userId: string) => {
    // BYPASS TEMPORÁRIO PARA DESBLOQUEAR A FASE 4
    // Assumimos que o usuário já tem onboarding concluído
    setHasOnboarded(true)
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const currentUser = session?.user ?? null
      setUser(currentUser)
      if (currentUser) {
        await checkOnboarding(currentUser.id)
      } else {
        setHasOnboarded(null)
      }
      setIsLoading(false)
    }).catch(err => {
      console.error('Failed to get session:', err)
      setUser(null)
      setHasOnboarded(null)
      setIsLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setIsLoading(true) // Tranca o roteador novamente
      const currentUser = session?.user ?? null
      setUser(currentUser)
      if (currentUser) {
        await checkOnboarding(currentUser.id)
      } else {
        setHasOnboarded(null)
      }
      setIsLoading(false) // Libera o roteador após carregar onboarding
    })

    return () => subscription.unsubscribe()
  }, [checkOnboarding])

  return (
    <AuthContext.Provider value={{ user, isLoading, hasOnboarded }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
